import React from "react";
import { X } from "lucide-react";

export const btn = {
  primary: "inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground text-sm font-semibold hover:bg-accent transition-colors disabled:opacity-50",
  outline: "inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-primary text-sm font-semibold hover:bg-secondary transition-colors disabled:opacity-50",
  ghost: "inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium hover:bg-secondary transition-colors",
  danger: "inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-destructive text-destructive text-sm font-semibold hover:bg-destructive hover:text-destructive-foreground transition-colors",
};

export const input = "w-full border border-input bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background";

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
      <div>
        <h1 className="text-3xl font-light tracking-tighter">{title}</h1>
        {subtitle && <p className="text-muted-foreground mt-1">{subtitle}</p>}
      </div>
      {children && <div className="flex items-center gap-2">{children}</div>}
    </div>
  );
}

export function Stat({ label, value, sub, accent }) {
  return (
    <div className="bg-card border border-border p-6 min-w-0" data-testid={`stat-${(label || "").toLowerCase().replace(/\s+/g, "-")}`}>
      <p className="overline text-muted-foreground">{label}</p>
      <p className={`text-3xl font-black tracking-tight mt-2 tabular-nums ${accent ? "text-accent" : ""}`}>{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1 break-words">{sub}</p>}
    </div>
  );
}

export function Field({ label, children, hint }) {
  return (
    <div>
      <label className="text-sm font-medium">{label}</label>
      <div className="mt-1.5">{children}</div>
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}

export function Modal({ open, onClose, title, children, testid }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4" data-testid={testid}>
      <div className="absolute inset-0 bg-foreground/50" onClick={onClose} />
      <div className="relative bg-card border border-border w-full max-w-lg max-h-[90vh] overflow-y-auto thin-scroll">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-lg font-bold">{title}</h2>
          <button onClick={onClose} data-testid="modal-close" className="hover:bg-secondary p-1"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

export function EmptyState({ icon: Icon, title, subtitle, children }) {
  return (
    <div className="border border-dashed border-border bg-card p-12 text-center" data-testid="empty-state">
      {Icon && <Icon className="h-10 w-10 text-muted-foreground mx-auto" />}
      <h3 className="text-lg font-semibold mt-4">{title}</h3>
      {subtitle && <p className="text-muted-foreground mt-1 max-w-md mx-auto">{subtitle}</p>}
      {children && <div className="mt-6 flex justify-center">{children}</div>}
    </div>
  );
}

export function StatusBadge({ status }) {
  const map = {
    active: "bg-emerald-500/15 text-emerald-600",
    trial: "bg-accent/15 text-accent",
    completed: "bg-emerald-500/15 text-emerald-600",
    in_progress: "bg-accent/15 text-accent",
    pending_approval: "bg-amber-500/15 text-amber-600",
    declined: "bg-destructive/15 text-destructive",
    canceled: "bg-destructive/15 text-destructive",
    suspended: "bg-destructive/15 text-destructive",
    past_due: "bg-amber-500/15 text-amber-600",
  };
  return (
    <span className={`inline-flex items-center text-xs px-2.5 py-1 font-medium capitalize ${map[status] || "bg-secondary"}`}>
      {(status || "").replace("_", " ")}
    </span>
  );
}
